import React, { FC, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircularProgress } from '@mui/material';
import { Image } from 'common/contracts';
import { EAssetType } from 'constants/enums';
import { ProjectCategory } from 'views/Projects/constants';
import * as z from 'zod';

import { Form } from 'components/ui';

import useImages from '../../../api/useImages';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import Button from '../../Button/Button';
import Input from '../../Input/Input';
import { Tabs, TabsList } from '../../Tabs/Tabs';
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle
} from '../../ui';
import { TabsContent } from '../../ui/tabs';

import {
  EImageUploadModalTabs,
  IMAGE_UPLOAD_TABS,
  IMG_UPLOAD_FORM_SCHEMA
} from './constants';
import MediaContent from './MediaContent/MediaContent';
import UploadContent from './UploadContent/UploadContent';

type ImageUploadModalProps = {
  onClose: () => void;
  onUpload: (url: string, name: string, mimeType: string) => void;
  selectedImageUrl?: string;
  forceType?: EAssetType;
  uploadOnly?: boolean;
  uploadContext?: ProjectCategory;
  hideSuccessToast?: boolean;
  'data-testid'?: string;
  fallbackPublisherId?: string;
};

const UploadImageModal: FC<ImageUploadModalProps> = ({
  onClose,
  onUpload,
  selectedImageUrl,
  forceType,
  uploadOnly,
  uploadContext,
  hideSuccessToast = false,
  'data-testid': dataTestId,
  fallbackPublisherId
}) => {
  const { publisherId } = useParams();
  const { addImage, getImages, deleteImage } = useImages(
    publisherId || fallbackPublisherId,
    uploadContext
  );

  const [selectedUrl, setSelectedUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [isCopying, setIsCopying] = useState(false);

  const initialValues = {
    type: forceType || EAssetType.GENERAL,
    mediaId: '',
    image: null,
    tab: EImageUploadModalTabs.UPLOAD
  };

  const imgUploadFormConfig = useForm<z.infer<typeof IMG_UPLOAD_FORM_SCHEMA>>({
    resolver: zodResolver(IMG_UPLOAD_FORM_SCHEMA),
    defaultValues: initialValues,
    mode: 'onSubmit'
  });

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid, isDirty }
  } = imgUploadFormConfig;

  const watchedTab = watch('tab');

  useEffect(() => {
    setSelectedUrl(selectedImageUrl ?? '');
    if (selectedImageUrl) {
      setValue('tab', EImageUploadModalTabs.MEDIA);
    } else {
      setValue('tab', EImageUploadModalTabs.UPLOAD);
    }
  }, [selectedImageUrl, setValue]);

  const handleSelectImage = (url: string) => {
    setSelectedUrl(url);
  };

  const handleImgUploadFormSubmit = async (
    data: z.infer<typeof IMG_UPLOAD_FORM_SCHEMA>
  ) => {
    try {
      if (data.tab === 'media' && selectedUrl) {
        const selectedImage = getImages.data?.find(
          (img: Image) => img.url === selectedUrl
        );
        onUpload(
          selectedUrl,
          selectedImage?.name || '',
          selectedImage?.mimeType || ''
        );
        onClose();
        setSearchQuery('');
        return;
      }
      if (data.tab === 'upload' && data.isExtrenalImageUrl) {
        const imageResponse = await addImage.mutateAsync({
          externalImageUrl: data.externalImageUrl,
          name: data.mediaId,
          type: data.type
        });
        onUpload(
          imageResponse.data.url,
          imageResponse.data.name,
          imageResponse.data.mimeType
        );
        onClose();
        setSearchQuery('');
        return;
      }
      const trimmedImageId = (data.mediaId as string).trim();
      const uploadConfig = {
        onUploadSuccess: () => {},
        uploadType: data.type,
        name: trimmedImageId
      };
      const file = data.image;
      if (!file) return;

      const formData = new FormData();
      formData.append('image', file as any);
      formData.append('type', uploadConfig.uploadType as string);
      formData.append('name', uploadConfig.name);

      await addImage.mutateAsync(formData);
      const refetchResult = await getImages.refetch();
      const updatedImages = refetchResult.data;

      const uploadedImage = updatedImages?.find(
        (img: Image) => img.name === uploadConfig.name
      );
      if (uploadedImage?.url) {
        onUpload(uploadedImage.url, uploadedImage.name, uploadedImage.mimeType);
      }

      if (!hideSuccessToast) {
        showToast({
          message: 'File has been uploaded successfully',
          status: EToasterStatus.SUCCESS
        });
      }
      reset(initialValues);
      onClose();
      setSearchQuery('');
    } catch (error: any) {
      showToast({
        message:
          error?.response?.data?.message ||
          'File has failed to upload. Please try again',
        status: EToasterStatus.ERROR
      });
    }
  };

  const handleImageDelete = async (imageId: string) => {
    deleteImage.mutate(imageId, {
      onSuccess: () => {
        const deletedItemName =
          getImages.data.find((item: any) => {
            return item.mediaId === imageId;
          })?.name ?? '';
        getImages.refetch();
        showToast({
          message: `${deletedItemName} has been deleted`,
          status: EToasterStatus.SUCCESS
        });
      },
      onError: (data: any) => {
        const errorMsg = `${data.response.data.message} ${data.response.data.data ? data.response.data.data.join(',') : ''}`;
        showToast({
          message: errorMsg,
          status: EToasterStatus.ERROR
        });
      }
    });
  };

  if (getImages.isError) {
    showToast({
      message: 'Could not load media list from server',
      status: EToasterStatus.ERROR
    });
  }

  useEffect(() => {
    if (isCopying) {
      showToast({
        message: 'Asset URL copied to clipboard',
        status: EToasterStatus.SUCCESS
      });
      setIsCopying(false);
    }
  }, [isCopying, setIsCopying]);

  return (
    <Form {...imgUploadFormConfig}>
      <DialogPortal>
        <DialogContent className="bg-base-card overflow-hidden p-0 max-w-[646px] w-full gap-0 max-h-[745px]">
          <DialogHeader className="bg-base-muted py-4 px-6 flex flex-row items-center h-[58px] justify-between">
            <DialogTitle className="text-base-foreground text-p-l font-semibold">
              Upload Image
            </DialogTitle>
            <DialogClose
              asChild
              onClick={() => setSelectedUrl(selectedImageUrl ?? '')}
            >
              <FontAwesomeIcon
                icon="fa-regular fa-xmark"
                className="!mt-0 text-base-secondaryForeground cursor-pointer"
              />
            </DialogClose>
          </DialogHeader>

          <Tabs
            defaultValue={EImageUploadModalTabs.UPLOAD}
            value={watchedTab}
            onValueChange={(value) =>
              setValue('tab', value as EImageUploadModalTabs)
            }
            className="p-6"
          >
            {!uploadOnly && (
              <div className="flex items-center">
                <TabsList tabs={IMAGE_UPLOAD_TABS} className="mb-4" />
                <TabsContent
                  value={EImageUploadModalTabs.MEDIA}
                  className="mt-0 mb-4 ml-auto"
                >
                  <Input
                    rightIcon={
                      <FontAwesomeIcon icon="fa-regular fa-magnifying-glass" />
                    }
                    placeholder="Search"
                    className="h-10"
                    isHorizontal
                    onChange={(e) => setSearchQuery(e.target.value)}
                    value={searchQuery}
                  />
                </TabsContent>
              </div>
            )}

            <TabsContent value={EImageUploadModalTabs.UPLOAD} className="mt-0">
              <UploadContent
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
                forceType={forceType}
              />
            </TabsContent>

            <TabsContent value={EImageUploadModalTabs.MEDIA} className="mt-0">
              {getImages.isLoading ? (
                <div className="flex items-center justify-center max-h-[600px] w-full">
                  <CircularProgress className="top-1/2 left-1/2 h-16 w-16" />
                </div>
              ) : (
                <MediaContent
                  images={getImages.data}
                  searchQuery={searchQuery}
                  forceType={forceType}
                  handleImageDelete={handleImageDelete}
                  selectedImageUrl={selectedUrl}
                  onSelectImage={handleSelectImage}
                  onDoubleClick={handleSubmit(handleImgUploadFormSubmit)}
                  setIsCopying={setIsCopying}
                />
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="bg-base-muted p-6 max-h-[84px]">
            <DialogClose asChild>
              <Button type="button" variant="link">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={
                watchedTab === EImageUploadModalTabs.MEDIA
                  ? !selectedUrl
                  : !isValid
              }
              onClick={handleSubmit(handleImgUploadFormSubmit)}
              data-testid={dataTestId}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Form>
  );
};

export default UploadImageModal;
